import { combineReducers, Action } from 'redux'
import { configureStore } from '@reduxjs/toolkit'
import { ThunkAction } from 'redux-thunk'
import * as Sentry from '@sentry/react'

import mainReducer from '../slices/mainSlice'
import photoReducer from '../slices/photoSlice'
import groupReducer from '../slices/groupSlice'
import pathwayReducer from '../slices/pathwaySlice'
import photoWatermarkReducer from '../slices/photoWatermarkSlice'
import watermarkEditorReducer from '../slices/watermarkEditorSlice'
import folderReducer from '../slices/folderSlice'
import photoPickerReducer from '../slices/photoPickerSlice'
import authPolicyReducer from '../slices/authPolicySlice'
import actionTypes from '../constants/actionTypes'

import photosByLocationReducer from '../slices/photosByLocationSlice'
import photosByPreciseReducer from '../slices/photosByPreciseSlice'
import photosByContentReducer from '../slices/photosByContentSlice'
import allPhotoReducer from '../slices/allPhotoSlice'
import rectifyReducer from '../slices/rectifySlice'
import verifyAuthReducer from '../slices/verifyAuthSlice'
import groupLimitReducer from '../slices/groupLimitSlice'
import photoFilterReducer from '../slices/photoFilterSlice'
import watermarkReducer from '../slices/watermarkSlice'
import organizationReducer from '../slices/organizationSlice'
import mySpaceReducer from '../slices/mySpace'
import { aiClassifyReducer, streamMiddleware } from '../slices/aiClassify'
import folderQuotaReducer from '../slices/folderQuotaSlice'
import aiClerkSettingsReducer from '../slices/aiClerkSettingsSlice'
import aiClerkUserAskReducer from '../slices/aiClerkUserAskSlice'
import myseInspectReducer from '../slices/myseInspectSlice'
import taskFlowLandingReducer from '../slices/taskFlowLandingSlice'
import taskFlowRedDotReducer from '../slices/taskFlowRedDotSlice'

const rootReducer = combineReducers({
  main: mainReducer,
  photo: photoReducer,
  group: groupReducer,
  pathway: pathwayReducer,
  editor: watermarkEditorReducer,
  watermark: watermarkReducer,
  photoWatermark: photoWatermarkReducer,
  organization: organizationReducer,
  folder: folderReducer,
  photoPicker: photoPickerReducer,
  authPolicy: authPolicyReducer,
  photosByLocation: photosByLocationReducer,
  photosByPrecise: photosByPreciseReducer,
  photosByContent: photosByContentReducer,
  allPhoto: allPhotoReducer,
  rectify: rectifyReducer,
  verifyAuth: verifyAuthReducer,
  groupLimit: groupLimitReducer,
  photoFilter: photoFilterReducer,
  mySpace: mySpaceReducer,
  aiClassify: aiClassifyReducer,
  folderQuota: folderQuotaReducer,
  aiClerkSettings: aiClerkSettingsReducer,
  aiClerkUserAsk: aiClerkUserAskReducer,
  myseInspect: myseInspectReducer,
  // 拍照流程后台落地态(打开 web 时并行拉一次 /form/list 算好,页面直接读)
  taskFlowLanding: taskFlowLandingReducer,
  // 拍照流程侧边栏红点(复用上面那次 /form/list 的结果,不额外发请求)
  taskFlowRedDot: taskFlowRedDotReducer
})

const allowedActionTypes = [
  actionTypes.SET_USER,
  actionTypes.SET_PHOTOS_FILTER,
  actionTypes.SET_GROUP_INFO,
  actionTypes.RESET_FILTER,
  actionTypes.SET_EXPORT_TASKS,
  actionTypes.SET_GROUP_ID,
  actionTypes.SET_MY_ROLE,
  actionTypes.SET_GROUP_VIEW_PERM,
  actionTypes.SET_ONLY_IN_GROUP,
  actionTypes.SET_EXPORT_WATERMARK_FILTER,
  actionTypes.SET_EDITOR_WATERMARK,
  actionTypes.CLEAR_EDITOR
]

const sentryReduxEnhancer = Sentry.createReduxEnhancer({
  actionTransformer: (action) => {
    if (allowedActionTypes.includes(action.type)) {
      return action
    }
    return null
  },
  stateTransformer: (state) => {
    // main 已迁移到 RTK Slice，使用普通 JS 对象操作
    const {
      activeModules,
      isCheckingAuth,
      loading,
      platformSignModalVisible,
      ...mainRest
    } = state.main
    const mainUser = mainRest.user
      ? { ...mainRest.user, groups: undefined }
      : null
    const transformedState = {
      ...state,
      main: { ...mainRest, user: mainUser },
      photo: null,
      group: { ...state.group, members: undefined },
      pathway: null,
      photoWatermark: null,
      watermark: null,
      organization: null,
      editor: null,
      folder: null,
      photoPicker: null,
      allPhoto: null
    }
    return transformedState
  }
})

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      // Immutable.js 对象不可序列化，暂时禁用检查（Phase 2.2 迁移后移除）
      serializableCheck: false,
      immutableCheck: false
    }).concat(streamMiddleware),
  enhancers: (defaultEnhancers: any[]) => [
    ...defaultEnhancers,
    sentryReduxEnhancer
  ],
  devTools: process.env.NODE_ENV === 'development'
})

export type AppDispatch = any
export type RootState = ReturnType<typeof store.getState>

export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>
